/**
 * MinistrareIA - Onboarding Page
 * Multi-step onboarding flow (currently single step: channel selection)
 * 
 * UPDATED: Now redirects to /connection-setup instead of directly to inbox
 * 
 * Flow: Register → ChannelSelection → ConnectionSetup → AgentOnboarding → Inbox
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ChannelSelectionStep } from '../../components/onboarding';
import onboardingService from '../../services/global/onboardingService';

// Step configuration for future extensibility
const STEPS = [
  {
    id: 'channel-selection',
    title: 'Conexão',
    component: 'ChannelSelectionStep',
  },
  // Future steps:
  // { id: 'profile-setup', title: 'Perfil', component: 'ProfileSetupStep' },
  // { id: 'agent-config', title: 'Agente IA', component: 'AgentConfigStep' },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { colors, theme } = useTheme();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Handle channel selection
  const handleChannelSelect = async (channel) => {
    setLoading(true);
    setError('');

    try {
      // Save channel selection to backend
      const response = await onboardingService.selectChannel(channel);
      
      // Update user in context
      if (response.data?.user) {
        updateUser(response.data.user);
      }

      // ✅ UPDATED: Navigate to connection-setup instead of inbox
      // Pass channel in state so ConnectionSetup knows which flow to use
      navigate('/connection-setup', { 
        state: { channel },
        replace: true 
      });
      
    } catch (err) {
      console.error('Channel selection error:', err);
      setError(
        err.response?.data?.detail || 
        'Erro ao configurar canal. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Progress calculation
  const progressPercentage = ((currentStep + 1) / STEPS.length) * 100;

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 32px',
      backgroundColor: colors.bgPrimary,
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.textSecondary,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    logoIcon: {
      width: '36px',
      height: '36px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    logoText: {
      fontSize: '18px',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    progressBar: {
      width: '120px',
      height: '4px',
      backgroundColor: colors.borderColor,
      borderRadius: '2px',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.accentPrimary,
      borderRadius: '2px',
      transition: 'width 0.3s ease',
    },
    progressText: {
      fontSize: '13px',
      color: colors.textSecondary,
      fontWeight: 500,
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
    },
    stepContainer: {
      width: '100%',
      maxWidth: '900px',
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '10px',
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '24px',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '0 auto 24px',
    },
    // Stepper styles (for future multi-step)
    stepper: {
      display: 'none', // Hidden for single step
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '32px',
    },
    stepIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    stepDot: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: 600,
      transition: 'all 0.2s',
    },
    stepDotActive: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
    },
    stepDotCompleted: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
    },
    stepDotPending: {
      backgroundColor: colors.borderColor,
      color: colors.textTertiary,
    },
    stepLine: {
      width: '40px',
      height: '2px',
      backgroundColor: colors.borderColor,
    },
    stepLineCompleted: {
      backgroundColor: colors.accentPrimary,
    },
    stepLabel: {
      fontSize: '12px',
      color: colors.textSecondary,
      marginTop: '4px',
      textAlign: 'center',
    },
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <ChannelSelectionStep 
            onSelect={handleChannelSelect}
            loading={loading}
          />
        );
      // Future steps:
      // case 1: return <ProfileSetupStep />;
      // case 2: return <AgentConfigStep />;
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Content */}
      <main style={styles.content}>
        <div style={styles.stepContainer}>
          {/* Stepper - hidden for single step */}
          {STEPS.length > 1 && (
            <div style={styles.stepper}>
              {STEPS.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div style={styles.stepIndicator}>
                    <div style={{
                      ...styles.stepDot,
                      ...(index < currentStep ? styles.stepDotCompleted : 
                          index === currentStep ? styles.stepDotActive : 
                          styles.stepDotPending),
                    }}>
                      {index < currentStep ? '✓' : index + 1}
                    </div>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div style={{
                      ...styles.stepLine,
                      ...(index < currentStep ? styles.stepLineCompleted : {}),
                    }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Error Message */}
          {error && <div style={styles.error}>{error}</div>}

          {/* Current Step */}
          {renderStep()}
        </div>
      </main>
    </div>
  );
};

export default Onboarding;